import type { Mesh } from '@babylonjs/core';
import type { Basket } from '@easterngraphics/wcf/modules/bsk';
import type { ArticleManager } from '@easterngraphics/wcf/modules/cf';
import type { Application } from '@easterngraphics/wcf/modules/core';
import type { ExchangeRate } from '@easterngraphics/wcf/modules/eaiws/basket';
import type { AbstractEventListener} from '@easterngraphics/wcf/modules/utils';
import type { EaiwsBasket } from '@egr/xbox/base-app/EaiwsBaskets';

import { developmentMode } from './ReactScriptHelper';

import { SaveSessionOptions } from '@easterngraphics/wcf/modules/eaiws/session';
import { ListenableEvent } from '@easterngraphics/wcf/modules/utils';

export async function syncPricingProcedure(basket: EaiwsBasket, articleManager: ArticleManager) {
    if (articleManager.pricingProcedure?.name !== basket.calculation.activePricingProcedure) {
        if (basket.calculation.activePricingProcedure == null) {
            articleManager.setPricingProcedure(null);
        } else {
            const pricingProcedure = await basket.calculation.getPricingProcedure(basket.calculation.activePricingProcedure);
            articleManager.setPricingProcedure(pricingProcedure);
        }
    }
}

export function updateNavigationAreaByMesh(
    mesh: Mesh | null,
    coreApp: Application,
    minValue: number,
    override?: number,
) {
    if (mesh == null) {
        return;
    }

    // add 10 meter offset
    const radius: number = (Math.max(mesh.scaling.x, mesh.scaling.z) * 0.5) + 10.0;
    coreApp.viewer.view.cameraControl.setNavigationArea(
        override ?? (
            Math.max(
                radius,
                minValue,
                coreApp.viewer.view.cameraControl.navigationArea.radius
            )
        )
    );
}

export async function getObkUrl(basket: Basket): Promise<string> {
    const saveOptions: SaveSessionOptions = new SaveSessionOptions();
    saveOptions.export = true;

    if (basket.calculation.activePricingProcedure != null) {
        saveOptions.saveLegacyCalculation = true;
        saveOptions.legacyCalculationName = basket.calculation.activePricingProcedure;
    }

    return basket.context.session.saveSession(null, saveOptions);
}

export function get3dCanvasPixelRatio(initOptions: Application['initOptions'], fallback: number = 1.0): number {
    if (initOptions.adaptToDeviceRatio) {
        return Math.min(window.devicePixelRatio, initOptions.maximumDeviceRatio);
    }

    return fallback;
}

export class CreationEvent<T, O> extends ListenableEvent<T, O> {
    private _value: T | undefined;

    public override addListener(listener: AbstractEventListener<T, O, void>): void {
        if (this._value != null) {
            return listener(this._value, this.mOwner);
        }
        return super.addListener(listener);
    }

    public override trigger(parameter: T): void {
        if (this._value != null && developmentMode) {
            console.warn('trigger should only be called once');
        }
        this._value = parameter;
        super.trigger(parameter);
    }
}

export async function setFilteredExchangeRates(exchangeRates: Array<ExchangeRate>, basketForFilter: EaiwsBasket) {
    if (basketForFilter.calculation.activatePricingProcedure == null) {
        return;
    }
    const allowedRates = await basketForFilter.calculation.getExchangeRates();
    return basketForFilter.calculation.setExchangeRates(exchangeRates.filter((value) => {
        const allowedRate = allowedRates.find((rate) => rate.currency === value.currency);
        return allowedRate != null && value.rate != null && allowedRate.rate !== value.rate;
    }));
}